import React, { useMemo, useState } from 'react';
import styles from './FullReport.module.scss';
import {
  Customer,
  CustomerContext,
  User,
  UserContext
} from 'ui-smartforce-settings';
import { ReportValue } from '../../../Models';
import { ReportBody } from './ReportBody/ReportBody';
import { ReportHeader } from './ReportHeader/ReportHeader';
import { canAmendReport } from '../../../Helpers';
import { SFScrollable, SFScrollableRefHandler } from 'sfui';
import {
  ReportSectionSelect,
  ReportSectionSelectOption
} from './ReportSectionSelect/ReportSectionSelect';
import { CadReportBody } from './CadReportBody/CadReportBody';
import { CADRMSReportValueType } from '../../../Models/CadReportValue';

export type ReportSectionID = 'contact' | 'cad_rms';
export interface FullReportProps {
  report: ReportValue;
  onAmend: () => void;
}

export const FullReport = ({
  report,
  onAmend
}: FullReportProps): React.ReactElement<FullReportProps> => {
  const customer = React.useContext(CustomerContext).customer as Customer;
  const user = React.useContext(UserContext).user as User;
  const canAmend: boolean = canAmendReport(report, user, customer);
  const [hasCadRms, setHasCadRms] = useState<boolean>(!!report.cad_rms);
  const [selectedSection, setSelectedSection] = useState<ReportSectionID>(
    hasCadRms ? 'cad_rms' : 'contact'
  );

  React.useEffect(() => {
    const reportHasCadRms = !!report.cad_rms;
    setHasCadRms(reportHasCadRms);
    setSelectedSection(reportHasCadRms ? 'cad_rms' : 'contact');
  }, [report]);

  const options = useMemo(() => {
    const opts: Array<ReportSectionSelectOption> = [
      {
        sectionId: 'contact',
        sectionName: 'Contact',
        incidentDate: (report.general_information?.date as string) || '',
        statusChips: {
          isReportable:
            report.reportable && report.reportable.length > 0
              ? report.reportable[0]?.is_reportable
              : true,
          status: report.status
        }
      }
    ];

    if (hasCadRms)
      opts.unshift({
        sectionId: 'cad_rms',
        sectionName: 'CAD/RMS',
        incidentDate: report.cad_rms?.cad?.date_time.received || ''
      });

    return opts;
  }, [report, hasCadRms]);

  const onChange = (value: ReportSectionID) => {
    setSelectedSection(value);
    if (refReport.current) {
      refReport.current.scrollToTop();
    }
  };

  const refReport = React.useRef<SFScrollableRefHandler>(null);

  return (
    <div className={styles.fullReport}>
      <SFScrollable
        containerClassName={`${styles.scrollableContainer} ${
          !hasCadRms ? styles.noSelect : ''
        }`}
      >
        {hasCadRms && (
          <SFScrollable
            className={styles.scrollableSelect}
            containerClassName={styles.scrollableSelectContainer}
          >
            <ReportSectionSelect
              options={options}
              selected={selectedSection}
              onChange={onChange}
            />
          </SFScrollable>
        )}
        <SFScrollable className={styles.scrollableReport} ref={refReport}>
          {hasCadRms && (
            <div className={styles.mobileSelect}>
              <ReportSectionSelect
                options={options}
                selected={selectedSection}
                onChange={onChange}
              />
            </div>
          )}
          <ReportHeader
            customer={customer}
            canAmend={canAmend}
            hideAmendButton={selectedSection === 'cad_rms'}
            reportStatus={report.status}
            onAmend={onAmend}
          />
          {selectedSection === 'contact' && <ReportBody report={report} />}
          {selectedSection === 'cad_rms' && !!report.cad_rms && (
            <CadReportBody report={report.cad_rms as CADRMSReportValueType} />
          )}
        </SFScrollable>
      </SFScrollable>
    </div>
  );
};
